import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward, faMagnifyingGlass, faTrashCan, faPaperPlane, faHourglassHalf, faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { BADGE_ICON } from '../Competitions/BadgeShelf';
import { MESSAGES, firstOf } from './messages';
import './Nominate.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthName = (p) => {
  const m = /^(\d{4})-(\d{2})$/.exec(p || '');
  return m ? new Date(+m[1], +m[2] - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : p;
};

const Face = ({ src, name }) => {
  const [broken, setBroken] = useState(false);
  return <span className="rmnom-face">{src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : String(name || '?').charAt(0)}</span>;
};

const STATUS = {
  pending: { icon: faHourglassHalf, label: 'Waiting for approval', tone: 'is-wait' },
  approved: { icon: faCircleCheck, label: 'Approved 🎉', tone: 'is-ok' },
  rejected: { icon: faCircleXmark, label: 'Not approved', tone: 'is-no' },
};

/* Teachers: nominate one of their students for a badge; the admin approves it */
const Nominate = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [student, setStudent] = useState(null);
  const [search, setSearch] = useState('');
  const [badge, setBadge] = useState('');
  const [period, setPeriod] = useState(thisMonth);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [activeOnly, setActiveOnly] = useState(() => {
    try {
      return localStorage.getItem('rm-nominate-active') !== '0';
    } catch {
      return true;
    }
  });
  const toggleActive = (on) => {
    setActiveOnly(on);
    try {
      localStorage.setItem('rm-nominate-active', on ? '1' : '0');
    } catch {
      /* private mode */
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/teacher/awards/nominations`, auth());
      setData(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.status === 404 ? '' : 'We couldn’t load your students.');
      if (err.response?.status === 404) setData({ hidden: true });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // which months can be picked for this badge (month badges: 24th -> 3rd; Creative mind: any time)
  const win = data?.window || null;
  const allowed = win ? (badge === 'creative' ? win.creative : win.monthly) || [] : null;
  useEffect(() => {
    if (allowed && allowed.length && !allowed.includes(period)) setPeriod(allowed[0]);
  }, [allowed, period]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.students || []).filter((x) => (!activeOnly || x.active !== false) && (!q || x.name.toLowerCase().includes(q)));
  }, [data, search, activeOnly]);

  if (!data || data.hidden) return error ? <p className="rmnom-error">{error}</p> : null;

  const send = async () => {
    if (!student || !badge) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/teacher/awards/nominations`, { studentId: student.id, badge, period, note }, auth());
      setToast(`${student.name} nominated. The admin will review it 🙏`);
      setStudent(null);
      setBadge('');
      setNote('');
      setSearch('');
      load();
    } catch (err) {
      setToast(err.response?.data?.error || 'Couldn’t send the nomination.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Take back your nomination of ${n.student}?`)) return;
    try {
      await axios.delete(`${API}/api/teacher/awards/nominations/${n.id}`, auth());
      load();
    } catch {
      setToast('Couldn’t remove it.');
    }
  };

  const picked = (data.catalog || []).find((b) => b.key === badge);
  // this month's nominations (rejected ones don't count) against the limits
  const limits = data.limits || null;
  const used = (data.nominations || []).filter((n) => n.period === period && n.status !== 'rejected');
  const usedMonth = used.filter((n) => n.badge === 'student_month').length;
  const hiddenCount = activeOnly ? (data.students || []).filter((x) => x.active === false).length : 0;

  return (
    <section className="rmnom" aria-labelledby="rmnom-title">
      <div className="rmnom-head">
        <h2 id="rmnom-title">
          <FontAwesomeIcon icon={faAward} /> Nominate a student
        </h2>
        <p>Who deserves a badge this month? Nominate them, and the admin approves it.</p>
        {win && (
          <p className={`rmnom-window ${win.monthly.length ? 'is-open' : ''}`}>
            {win.monthly.length
              ? `Month badges are open for ${monthName(win.monthly[0])} until ${win.closesOn}.`
              : `Month badges open on ${win.opensOn} (from the ${win.openDay}th until the ${win.closeDay}rd). Creative mind can be sent any time.`}
          </p>
        )}
      </div>

      {!data.tableReady && <p className="rmnom-error">Nominations aren’t ready yet. Please tell the admin.</p>}

      <div className="rmnom-grid">
        <div>
          <h3>1. Student</h3>
          {student ? (
            <div className="rmnom-picked">
              <Face src={student.photo} name={student.name} />
              <b>{student.name}</b>
              <button type="button" onClick={() => setStudent(null)}>
                Change
              </button>
            </div>
          ) : (
            <>
              <label className="rmnom-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your students" aria-label="Search your students" />
              </label>
              <label className="rmnom-check">
                <input type="checkbox" checked={activeOnly} onChange={(e) => toggleActive(e.target.checked)} />
                <span>
                  Current active students only{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
                </span>
              </label>
              <ul className="rmnom-people">
                {matches.map((x) => (
                  <li key={x.id}>
                    <button type="button" onClick={() => setStudent(x)}>
                      <Face src={x.photo} name={x.name} />
                      <span>{x.name}</span>
                      <small>{x.level}</small>
                    </button>
                  </li>
                ))}
                {!matches.length && <li className="rmnom-dim">{data.students?.length ? 'Nobody found.' : 'No students with classes in the last 2 months.'}</li>}
              </ul>
            </>
          )}
        </div>

        <div>
          <h3>2. Badge</h3>
          <ul className="rmnom-badges" role="radiogroup" aria-label="Badge">
            {(data.catalog || []).map((b) => (
              <li key={b.key}>
                <button type="button" role="radio" aria-checked={badge === b.key} className={`is-${b.key} ${badge === b.key ? 'is-on' : ''}`} onClick={() => setBadge(b.key)} title={b.how}>
                  <FontAwesomeIcon icon={BADGE_ICON[b.icon] || faAward} /> {b.title}
                </button>
              </li>
            ))}
          </ul>
          {picked && <p className="rmnom-dim">{picked.how}</p>}

          <h3>3. Month and message</h3>
          <div className="rmnom-row">
            {allowed ? (
              <select value={allowed.includes(period) ? period : ''} onChange={(e) => setPeriod(e.target.value)} aria-label="Month" disabled={!allowed.length}>
                {!allowed.length && <option value="">Opens on {win.opensOn}</option>}
                {allowed.map((p) => (
                  <option key={p} value={p}>
                    {monthName(p)}
                  </option>
                ))}
              </select>
            ) : (
              <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Month" />
            )}
            <input type="text" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why does this student deserve it? (everyone sees it)" aria-label="Message" />
          </div>
          {badge && (MESSAGES[badge] || []).length > 0 && (
            <div className="rmnom-msgs">
              {MESSAGES[badge].map((t) => {
                const text = t.replace('{name}', firstOf(student?.name) || 'champ');
                return (
                  <button key={t} type="button" className={note === text ? 'is-on' : ''} onClick={() => setNote(text)}>
                    {text}
                  </button>
                );
              })}
            </div>
          )}

          {limits && (
            <p className="rmnom-limits">
              This month: <b>{used.length}</b> of {limits.perMonth} nominations · <b>{usedMonth}</b> of {limits.studentMonth} Student of the month ·
              one badge per student
            </p>
          )}
          <button type="button" className="rmnom-btn" disabled={!student || !badge || busy || !data.tableReady || (allowed && !allowed.includes(period))} onClick={send}>
            <FontAwesomeIcon icon={faPaperPlane} /> {busy ? 'Sending…' : 'Send nomination'}
          </button>
        </div>
      </div>

      {data.nominations?.length > 0 && (
        <>
          <h3 className="rmnom-mine-title">My nominations</h3>
          <ul className="rmnom-mine">
            {data.nominations.map((n) => {
              const st = STATUS[n.status] || STATUS.pending;
              return (
                <li key={n.id}>
                  <Face src={n.photo} name={n.student} />
                  <div>
                    <b>{n.student}</b> · {n.title} · {n.periodLabel}
                    {n.note && <p>“{n.note}”</p>}
                    {n.status === 'rejected' && n.reason && <p className="rmnom-reason">Admin: {n.reason}</p>}
                  </div>
                  <span className={`rmnom-status ${st.tone}`}>
                    <FontAwesomeIcon icon={st.icon} /> {st.label}
                  </span>
                  {n.status === 'pending' && (
                    <button type="button" className="rmnom-del" onClick={() => remove(n)} aria-label={`Take back the nomination of ${n.student}`}>
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {toast && (
        <div className="rmnom-toast" role="status">
          {toast}
        </div>
      )}
    </section>
  );
};

export default Nominate;
